import { useIntl } from "react-intl";

import { type SupportedLocale, LOCALE_LABELS, SUPPORTED_LOCALES } from "@/i18n";
import { useSession } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import { useLocaleStore } from "@/stores/locale";

/**
 * Minimal locale switcher — renders a <select> dropdown.
 *
 * - Authenticated users: persists to server via `settings.update`.
 * - Guests: persists to localStorage via Zustand.
 */
export function LocaleSwitcher({ className }: { className?: string }) {
  const intl = useIntl();
  const { data: sessionData } = useSession();
  const hasSession = !!sessionData?.session;

  const { data: settings } = trpc.settings.get.useQuery(undefined, {
    enabled: hasSession,
  });
  const utils = trpc.useUtils();
  const updateSettings = trpc.settings.update.useMutation({
    onSuccess: () => utils.settings.get.invalidate(),
  });

  const guestLocale = useLocaleStore((s) => s.locale);
  const setGuestLocale = useLocaleStore((s) => s.setLocale);

  const activeLocale = hasSession && settings ? settings.locale : guestLocale;

  function handleChange(newLocale: SupportedLocale) {
    if (hasSession) {
      updateSettings.mutate({ locale: newLocale });
    } else {
      setGuestLocale(newLocale);
    }
  }

  return (
    <select
      value={activeLocale}
      onChange={(e) => handleChange(e.target.value as SupportedLocale)}
      className={className}
      aria-label={intl.formatMessage({
        id: "localeSwitcher.label",
        defaultMessage: "Select language",
      })}
    >
      {SUPPORTED_LOCALES.map((loc) => (
        <option key={loc} value={loc}>
          {LOCALE_LABELS[loc]}
        </option>
      ))}
    </select>
  );
}
